import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  RefreshCw, Search, Inbox, AlertCircle, Mail, Reply, Forward,
  Paperclip, Send, X, ExternalLink, ChevronLeft, MessageCircle, Eye,
  Trash2, PenSquare,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import RichTextEditor from '../../components/RichTextEditor';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const formatRelative = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  const now = new Date();
  const diffH = (now - dt) / 3600000;
  if (diffH < 1) return `${Math.max(1, Math.floor((now - dt) / 60000))}m`;
  if (diffH < 24) return `${Math.floor(diffH)}h`;
  if (diffH < 24 * 7) return dt.toLocaleDateString('en-CA', { weekday: 'short' });
  return dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

export const CrmEmails = () => {
  const [config, setConfig] = useState(null);
  const [activeMailbox, setActiveMailbox] = useState('all');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [composer, setComposer] = useState(null); // { mode: 'reply'|'forward'|'new', email? }
  // Bulk-select state — keyed by `${mailbox_key}:${imap_uid}` for stable identity
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  // Load mailbox config
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/emails/config`, { withCredentials: true })
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ configured: false, mailboxes: [] }));
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (activeMailbox !== 'all') params.mailbox = activeMailbox;
      if (unreadOnly) params.unread = true;
      if (debounced) params.q = debounced;
      const res = await axios.get(`${API_URL}/api/admin/emails`, { params, withCredentials: true });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error('Could not load emails');
    } finally {
      setLoading(false);
    }
  }, [page, activeMailbox, unreadOnly, debounced]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Auto-poll every 60s
  useEffect(() => {
    const id = setInterval(() => { fetchList(); }, 60_000);
    return () => clearInterval(id);
  }, [fetchList]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/emails/refresh`, {}, { withCredentials: true });
      const errs = (res.data?.results || []).filter((r) => r.error);
      const fetched = (res.data?.results || []).reduce((s, r) => s + (r.stored || 0), 0);
      if (errs.length) {
        toast.error(`IMAP error — ${errs[0].error}`);
      } else {
        toast.success(`Fetched ${fetched} new email${fetched === 1 ? '' : 's'}`);
      }
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const openEmail = async (item) => {
    setSelected({ ...item, _loading: true });
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/emails/${item.mailbox_key}/${item.imap_uid}`,
        { withCredentials: true },
      );
      setSelected(res.data);
      // Mark read locally
      if (!item.seen) {
        await axios.post(
          `${API_URL}/api/admin/emails/${item.mailbox_key}/${item.imap_uid}/mark-read`,
          { seen: true },
          { withCredentials: true },
        );
        setItems((prev) => prev.map((x) =>
          x.mailbox_key === item.mailbox_key && x.imap_uid === item.imap_uid ? { ...x, seen: true } : x
        ));
      }
    } catch {
      toast.error('Could not load email');
    }
  };

  // Build a stable key for an email row used in the selection Set
  const keyOf = (it) => `${it.mailbox_key}:${it.imap_uid}`;

  const toggleSelect = useCallback((it) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = keyOf(it);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedKeys((prev) => {
      const allKeys = items.map(keyOf);
      const allSelected = allKeys.length > 0 && allKeys.every((k) => prev.has(k));
      if (allSelected) {
        const next = new Set(prev);
        allKeys.forEach((k) => next.delete(k));
        return next;
      }
      return new Set([...prev, ...allKeys]);
    });
  }, [items]);

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  // Drop selection rows that are no longer on the visible page (e.g. after a
  // page change / mailbox switch). Keeps the bar from showing stale counts.
  useEffect(() => {
    setSelectedKeys((prev) => {
      if (!prev.size) return prev;
      const visible = new Set(items.map(keyOf));
      const next = new Set();
      prev.forEach((k) => { if (visible.has(k)) next.add(k); });
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const handleDelete = useCallback(async (rows, opts = {}) => {
    if (!rows || !rows.length) return;
    const list = Array.isArray(rows) ? rows : [rows];
    const labelCount = list.length === 1
      ? `"${(list[0].subject || '(no subject)').slice(0, 40)}"`
      : `${list.length} emails`;
    if (!opts.skipConfirm && !window.confirm(`Delete ${labelCount}? This removes the email from both this CRM and the IONOS mailbox. This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      const payload = list.map((r) => ({ mailbox_key: r.mailbox_key, imap_uid: r.imap_uid }));
      const res = await axios.post(
        `${API_URL}/api/admin/emails/delete`,
        { items: payload },
        { withCredentials: true },
      );
      const local = res.data?.deleted_local || 0;
      const remote = res.data?.deleted_remote || 0;
      const errs = res.data?.remote_errors || [];
      // Optimistically remove from list + drop from selection
      const dead = new Set(payload.map((p) => `${p.mailbox_key}:${p.imap_uid}`));
      setItems((prev) => prev.filter((it) => !dead.has(keyOf(it))));
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        dead.forEach((k) => next.delete(k));
        return next;
      });
      if (selected && dead.has(`${selected.mailbox_key}:${selected.imap_uid}`)) {
        setSelected(null);
      }
      if (errs.length) {
        toast.warning(`Deleted ${local} locally · IONOS sync failed for some (${errs[0]})`);
      } else if (remote === local) {
        toast.success(`Deleted ${local} email${local === 1 ? '' : 's'}`);
      } else {
        toast.success(`Deleted ${local} email${local === 1 ? '' : 's'} (${remote} from server)`);
      }
      // Refresh totals from the server in the background
      setTimeout(() => fetchList(), 250);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }, [selected, fetchList]);

  const selectedRows = useMemo(
    () => items.filter((it) => selectedKeys.has(keyOf(it))),
    [items, selectedKeys],
  );
  const allSelected = items.length > 0 && items.every((it) => selectedKeys.has(keyOf(it)));
  const someSelected = selectedKeys.size > 0;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4 max-w-7xl" data-testid="crm-emails">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Emails</h1>
        <p className="text-[#6B6B7A] text-sm mt-1">
          Unified inbox for all customer emails sent to your business addresses.
        </p>
      </motion.div>

      {!config?.configured && (
        <div className="bg-[#FFF1DC] border-2 border-[#F5C15A] rounded-2xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#B3770A] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[#8A5A1F]">Inbox not configured</p>
            <p className="text-xs text-[#8A5A1F]/80 mt-1">
              IMAP credentials are missing or invalid. Add IMAP_INFO_PASSWORD and IMAP_BOOKINGS_PASSWORD to backend/.env.
            </p>
          </div>
        </div>
      )}

      {config?.configured && config?.last_sync?.results?.some((r) => r.error) && (
        <div className="bg-[#FFF1DC] border-2 border-[#F5C15A] rounded-2xl p-4 flex items-start gap-3" data-testid="inbox-error-banner">
          <AlertCircle className="w-5 h-5 text-[#B3770A] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-[#8A5A1F]">IMAP authentication failed</p>
            <p className="text-xs text-[#8A5A1F]/80 mt-1">
              IONOS rejected the password. Common fixes:
            </p>
            <ul className="text-xs text-[#8A5A1F]/80 mt-1 list-disc ml-5 space-y-0.5">
              <li>Confirm IMAP/POP access is enabled for both mailboxes in IONOS Mail Control Panel → Mailbox → Access</li>
              <li>Some IONOS plans require the autogenerated mailbox username (e.g. <code>m9876543-1</code>) instead of the email address</li>
              <li>If 2-factor authentication is enabled, generate an "app password" inside IONOS</li>
              <li>Re-verify the password by logging into webmail.ionos.ca with the same credentials</li>
            </ul>
          </div>
        </div>
      )}

      {/* Mailbox tabs + actions */}
      <div className="flex flex-wrap items-center gap-2 justify-between bg-white rounded-2xl border border-[#E8E5DF] p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <MailboxPill
            id="all"
            label="All inboxes"
            color="#FF3B6B"
            active={activeMailbox === 'all'}
            onClick={() => { setActiveMailbox('all'); setPage(1); }}
          />
          {(config?.mailboxes || []).map((m) => (
            <MailboxPill
              key={m.key}
              id={m.key}
              label={m.label}
              color={m.color}
              active={activeMailbox === m.key}
              onClick={() => { setActiveMailbox(m.key); setPage(1); }}
            />
          ))}
          <button
            type="button"
            onClick={() => { setUnreadOnly((v) => !v); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              unreadOnly ? 'bg-[#1E1E24] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
            }`}
            data-testid="emails-toggle-unread"
          >
            Unread only
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#6B6B7A] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search subject, sender, body…"
              className="h-8 pl-8 rounded-xl border-[#E8E5DF] text-xs w-64"
              data-testid="emails-search"
            />
          </div>
          <Button
            onClick={() => setComposer({ mode: 'new' })}
            disabled={!config?.configured}
            size="sm"
            className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="emails-compose-btn"
          >
            <PenSquare className="w-3.5 h-3.5 mr-1" />
            Compose
          </Button>
          <Button
            onClick={refresh}
            disabled={refreshing}
            size="sm"
            variant="outline"
            className="rounded-full text-xs border-[#E8E5DF]"
            data-testid="emails-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bulk action bar — appears when at least one row is selected */}
      {someSelected && (
        <div
          className="flex items-center justify-between gap-3 bg-[#FFF1F4] border-2 border-[#FF3B6B]/40 rounded-2xl px-4 py-2.5"
          data-testid="emails-bulk-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <span className="font-semibold text-[#FF3B6B]" data-testid="emails-bulk-count">
              {selectedKeys.size} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-[#6B6B7A] hover:text-[#1E1E24]"
              data-testid="emails-bulk-clear"
            >
              Clear selection
            </button>
          </div>
          <Button
            size="sm"
            onClick={() => handleDelete(selectedRows)}
            disabled={deleting}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs"
            data-testid="emails-bulk-delete"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {deleting ? 'Deleting…' : `Delete ${selectedKeys.size}`}
          </Button>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="emails-list">
        {loading ? (
          <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 flex flex-col items-center text-center">
            <Inbox className="w-10 h-10 text-[#E8E5DF] mb-2" />
            <p className="text-sm font-semibold text-[#1E1E24]">No emails yet</p>
            <p className="text-xs text-[#6B6B7A] mt-1">
              {config?.configured ? 'Click Refresh to fetch from IONOS' : 'Configure IMAP credentials to see emails here'}
            </p>
          </div>
        ) : (
          <>
            {/* List header — select-all */}
            <div className="px-4 py-2 border-b border-[#F0EDE6] bg-[#FFFDF8] flex items-center gap-3 text-[11px] uppercase tracking-wider font-semibold text-[#6B6B7A]">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                data-testid="emails-select-all"
                aria-label={allSelected ? 'Deselect all' : 'Select all'}
              />
              <span>{items.length} on this page</span>
            </div>
            <ul className="divide-y divide-[#F0EDE6]">
              {items.map((it) => {
                const k = keyOf(it);
                return (
                  <EmailRow
                    key={k}
                    email={it}
                    config={config}
                    selected={selectedKeys.has(k)}
                    onToggleSelect={() => toggleSelect(it)}
                    onDelete={() => handleDelete(it)}
                    onOpen={() => openEmail(it)}
                  />
                );
              })}
            </ul>
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-[#6B6B7A]">
          <span>{total} email{total === 1 ? '' : 's'} · Page {page} / {totalPages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded-full">Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded-full">Next</Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <EmailDetail
        email={selected}
        onClose={() => setSelected(null)}
        onReply={() => setComposer({ mode: 'reply', email: selected })}
        onForward={() => setComposer({ mode: 'forward', email: selected })}
        onDelete={() => handleDelete(selected)}
      />

      {/* Composer dialog (reply / forward / new) */}
      <ComposerDialog
        composer={composer}
        config={config}
        onClose={() => setComposer(null)}
        onSent={() => { setComposer(null); fetchList(); }}
      />
    </div>
  );
};

const MailboxPill = ({ id, label, color, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${active ? 'text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'}`}
    style={active ? { background: color } : {}}
    data-testid={`emails-tab-${id}`}
  >
    {label}
  </button>
);

const EmailRow = ({ email, config, selected, onToggleSelect, onDelete, onOpen }) => {
  const meta = useMemo(
    () => (config?.mailboxes || []).find((m) => m.key === email.mailbox_key) || {},
    [config, email.mailbox_key],
  );
  const fromName = email.from?.name || email.from?.email || 'Unknown';
  const subject = email.subject || '(no subject)';
  const preview = (email.body_text || '').slice(0, 120).replace(/\s+/g, ' ');
  const stop = (e) => e.stopPropagation();
  return (
    <li
      onClick={onOpen}
      className={`px-4 py-3 cursor-pointer hover:bg-[#FFFDF8] transition flex items-center gap-3 ${
        selected ? 'bg-[#FFF1F4]' : email.seen ? '' : 'bg-[#FFFDF8]/60'
      }`}
      data-testid={`email-row-${email.imap_uid}`}
    >
      <span onClick={stop} role="presentation">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggleSelect}
          data-testid={`email-row-select-${email.imap_uid}`}
          aria-label="Select email"
        />
      </span>
      <span
        className="w-1.5 h-10 rounded-full flex-shrink-0"
        style={{ background: email.seen ? 'transparent' : meta.color || '#FF3B6B' }}
        title={email.seen ? 'Read' : 'Unread'}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm truncate ${email.seen ? 'text-[#1E1E24]' : 'font-bold text-[#1E1E24]'}`}>
            {fromName}
          </p>
          <Badge variant="outline" className="text-[10px] border-[#E8E5DF]" style={{ color: meta.color, borderColor: `${meta.color}55` }}>
            {meta.label || email.mailbox_key}
          </Badge>
          {email.linked_booking_id && (
            <Badge className="text-[10px] bg-[#06D6A0]/15 text-[#06D6A0] border-none">customer</Badge>
          )}
          {email.attachments?.length > 0 && <Paperclip className="w-3 h-3 text-[#6B6B7A]" />}
        </div>
        <p className={`text-sm truncate ${email.seen ? 'text-[#6B6B7A]' : 'font-semibold text-[#1E1E24]'}`}>{subject}</p>
        {preview && <p className="text-xs text-[#6B6B7A] truncate mt-0.5">{preview}</p>}
      </div>
      <span className="text-xs text-[#6B6B7A] whitespace-nowrap flex-shrink-0">{formatRelative(email.date)}</span>
      <button
        type="button"
        onClick={(e) => { stop(e); onDelete?.(); }}
        className="p-1.5 rounded-full text-[#6B6B7A] hover:bg-[#FF3B6B]/10 hover:text-[#FF3B6B] transition opacity-60 hover:opacity-100"
        data-testid={`email-row-delete-${email.imap_uid}`}
        title="Delete email"
        aria-label="Delete email"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
};

const EmailDetail = ({ email, onClose, onReply, onForward, onDelete }) => {
  if (!email) return null;
  const isLoading = email._loading;
  return (
    <Dialog open={!!email} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="email-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{email.subject || '(no subject)'}</DialogTitle>
          <DialogDescription className="space-y-1 mt-1">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-[#1E1E24]">{email.from?.name || email.from?.email}</span>
              <span className="text-[#6B6B7A]">&lt;{email.from?.email}&gt;</span>
            </div>
            <div className="text-xs text-[#6B6B7A]">
              To: {(email.to || []).map((x) => x.email).join(', ')}
              {email.cc?.length ? ` · Cc: ${email.cc.map((x) => x.email).join(', ')}` : ''}
            </div>
            <div className="text-xs text-[#6B6B7A]">{email.date_raw}</div>
            {email.linked_booking_id && (
              <div className="text-xs">
                Linked customer: <strong>{email.linked_customer_name}</strong> ·{' '}
                <a href={`/crm/bookings?booking=${email.linked_booking_id}`} target="_blank" rel="noreferrer" className="text-[#FF3B6B] hover:underline">
                  view booking <ExternalLink className="w-3 h-3 inline" />
                </a>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="border-t border-[#F0EDE6] pt-4 min-h-[200px]">
          {isLoading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-[#FF3B6B]" /></div>
          ) : email.body_html ? (
            <iframe
              title="email-body"
              srcDoc={email.body_html}
              sandbox=""
              className="w-full min-h-[420px] border-0"
              data-testid="email-body-iframe"
            />
          ) : (
            <pre className="text-sm whitespace-pre-wrap font-sans text-[#1E1E24]">{email.body_text || '(empty body)'}</pre>
          )}
        </div>

        {email.attachments?.length > 0 && (
          <div className="border-t border-[#F0EDE6] pt-3 mt-3">
            <p className="text-[11px] uppercase font-semibold text-[#6B6B7A] tracking-wider mb-2">Attachments</p>
            <div className="flex flex-wrap gap-2">
              {email.attachments.map((a) => (
                <a
                  key={a.attachment_id}
                  href={`${API_URL}/api/admin/emails/attachments/${a.attachment_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFFDF8] border border-[#E8E5DF] rounded-full text-xs text-[#1E1E24] hover:bg-white hover:border-[#FF3B6B]"
                >
                  <Paperclip className="w-3 h-3" /> {a.filename}{' '}
                  <span className="text-[#6B6B7A]">({Math.round((a.size || 0) / 1024)}KB)</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
          <Button
            variant="outline"
            onClick={onDelete}
            className="rounded-full text-xs border-[#FF3B6B]/40 text-[#FF3B6B] hover:bg-[#FF3B6B]/10"
            data-testid="email-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
          <Button variant="outline" onClick={onForward} className="rounded-full text-xs" data-testid="email-forward-btn">
            <Forward className="w-3.5 h-3.5 mr-1" /> Forward
          </Button>
          <Button onClick={onReply} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="email-reply-btn">
            <Reply className="w-3.5 h-3.5 mr-1" /> Reply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ComposerDialog = ({ composer, config, onClose, onSent }) => {
  const [fromMailbox, setFromMailbox] = useState('info');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);

  // Default the From: mailbox to the first configured one
  useEffect(() => {
    if (!composer) return;
    const firstConfigured = (config?.mailboxes || []).find((m) => m.configured);
    if (firstConfigured) setFromMailbox(firstConfigured.key);
  }, [composer, config]);

  useEffect(() => {
    if (!composer) return;
    const e = composer.email || {};
    if (composer.mode === 'reply') {
      setFromMailbox(e.mailbox_key || 'info');
      setTo(e.from?.email || '');
      setCc('');
      const s = e.subject || '';
      setSubject(s.toLowerCase().startsWith('re:') ? s : `Re: ${s}`);
      // Build a Gmail-style quote header + the original body
      const fromName = e.from?.name || e.from?.email || 'sender';
      const fromAddr = e.from?.email ? ` &lt;${e.from.email}&gt;` : '';
      const dateStr = e.date ? new Date(e.date).toLocaleString('en-CA') : '';
      const original = e.body_html || (e.body_text ? `<pre style="white-space:pre-wrap;font-family:inherit">${e.body_text.replace(/[<>&]/g, (c) => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[c]))}</pre>` : '');
      setBodyHtml(
        `<p></p><p><br></p>` +
        `<blockquote>` +
        `<p style="color:#6B6B7A;font-size:12px;margin-bottom:6px">On ${dateStr}, ${fromName}${fromAddr} wrote:</p>` +
        original +
        `</blockquote>`
      );
    } else if (composer.mode === 'forward') {
      setFromMailbox(e.mailbox_key || 'info');
      setTo('');
      setCc('');
      const s = e.subject || '';
      setSubject(s.toLowerCase().startsWith('fwd:') ? s : `Fwd: ${s}`);
      // Forward includes a "begin forwarded message" header similar to Apple Mail
      const fromName = e.from?.name || e.from?.email || '';
      const fromAddr = e.from?.email ? ` &lt;${e.from.email}&gt;` : '';
      const dateStr = e.date ? new Date(e.date).toLocaleString('en-CA') : '';
      const original = e.body_html || (e.body_text ? `<pre style="white-space:pre-wrap;font-family:inherit">${e.body_text.replace(/[<>&]/g, (c) => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[c]))}</pre>` : '');
      setBodyHtml(
        `<p></p><p><br></p>` +
        `<p style="color:#6B6B7A;font-size:12px"><strong>---------- Forwarded message ----------</strong><br>` +
        `From: ${fromName}${fromAddr}<br>` +
        `Date: ${dateStr}<br>` +
        `Subject: ${e.subject || ''}</p>` +
        original
      );
    } else if (composer.mode === 'new') {
      // From is set by the effect above; reset everything else.
      setTo('');
      setCc('');
      setSubject('');
      setBodyHtml('<p></p>');
    }
  }, [composer]);

  if (!composer) return null;

  const isNew = composer.mode === 'new';
  const configuredMailboxes = (config?.mailboxes || []).filter((m) => m.configured);

  const send = async () => {
    if (!to.trim()) { toast.error('Recipient required'); return; }
    if (isNew && !subject.trim()) { toast.error('Subject required'); return; }
    setSending(true);
    try {
      const toList = to.split(',').map((x) => x.trim()).filter(Boolean);
      const ccList = cc.split(',').map((x) => x.trim()).filter(Boolean);
      if (isNew) {
        await axios.post(
          `${API_URL}/api/admin/emails/compose`,
          {
            from_mailbox: fromMailbox,
            to: toList,
            cc: ccList,
            subject,
            body_html: bodyHtml,
          },
          { withCredentials: true },
        );
        toast.success('Email sent');
      } else {
        const url = composer.mode === 'reply'
          ? `${API_URL}/api/admin/emails/${composer.email.mailbox_key}/${composer.email.imap_uid}/reply`
          : `${API_URL}/api/admin/emails/${composer.email.mailbox_key}/${composer.email.imap_uid}/forward`;
        await axios.post(
          url,
          { to: toList, cc: ccList, subject, body_html: bodyHtml },
          { withCredentials: true },
        );
        toast.success(composer.mode === 'reply' ? 'Reply sent' : 'Forwarded');
      }
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const titleLabel = isNew ? 'New Email' : (composer.mode === 'reply' ? 'Reply' : 'Forward');
  const TitleIcon = isNew ? PenSquare : (composer.mode === 'reply' ? Reply : Forward);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="email-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TitleIcon className="w-5 h-5 text-[#FF3B6B]" />
            {titleLabel}
            {!isNew && (
              <span className="text-xs text-[#6B6B7A] font-normal">
                from {composer.email.mailbox_email}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isNew && (
            <div>
              <label className="text-xs font-semibold text-[#1E1E24]">From</label>
              <select
                value={fromMailbox}
                onChange={(e) => setFromMailbox(e.target.value)}
                className="mt-1 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF3B6B]/40"
                data-testid="composer-from-mailbox"
              >
                {configuredMailboxes.length === 0 && (
                  <option value="">(no mailbox configured)</option>
                )}
                {configuredMailboxes.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.email || m.label} ({m.key}@)
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#1E1E24]">To</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="comma-separated" className="rounded-xl border-[#E8E5DF] mt-1" data-testid="composer-to" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1E1E24]">Cc <span className="font-normal text-[#6B6B7A]">(optional)</span></label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} className="rounded-xl border-[#E8E5DF] mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1E1E24]">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="composer-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1E1E24]">Message</label>
            <RichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              placeholder={composer.mode === 'reply' ? 'Write your reply…' : composer.mode === 'forward' ? 'Add a note before forwarding…' : 'Compose your email…'}
              minHeight={260}
              testId="composer-body-rte"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={send} disabled={sending} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="composer-send">
            <Send className="w-3.5 h-3.5 mr-1" /> {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CrmEmails;
